import { AfterViewInit, Component, DestroyRef, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit, ViewChild, computed, effect, inject, signal } from '@angular/core';
import {XploreItemService} from '../../../services/xplore-item.service';
import {Category, CommercialDTO, EventDTO, ExploreDTO, SimpleXploreItemDTO, TagDTO, UserAccountDTO, XploreItemDTO} from '../../../shared/object/xplorer-be-parsed-classes';
import {catchError, EMPTY, tap} from 'rxjs';
import {ActivatedRoute, Router} from "@angular/router";
import {MetaDataService} from "../../../services/meta/meta-data.service";
import { Location } from '@angular/common';
import {AnalyticsService} from "../../../services/analytics/analytics.service";
import {TerritoryService} from "../../../services/geolocation/territory.service";
import {environment} from "../../../../environments/environment";
import {ShareService} from "../../../services/share/share.service";
import {AuthService} from "../../../services/auth/auth.service";
import {UserService} from "../../../services/user/user.service";
import {UserGeolocationService, UserLocationStatus} from "../../../services/geolocation/user-geolocation.service";
import KeenSlider, { KeenSliderInstance } from "keen-slider"
import {ImageManager} from "../../../shared/image-manager";
import {createFeedSliderOptions} from "../../../shared/keen-slider-options";
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { MobileItemCardComponent } from '../../components/mobile-item-card/mobile-item-card.component';
import { BookSectionComponent } from '../../components/book-section/book-section.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DatePipe, NgOptimizedImage, isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

type DistanceChipState = 'loading' | 'ready' | 'unavailable';
type MobileOverlayTab = 'description' | 'info' | 'related' | 'booking';

const MOBILE_PANEL_MIN_COLLAPSED_HEIGHT = 106;
const MOBILE_PANEL_MAX_COLLAPSED_HEIGHT = 220;
const SWIPE_HINT_STORAGE_KEY = 'xplorer-feed-swipe-hint-seen';

@Component({
    selector: 'app-xplore-page',
    templateUrl: './xplore-page.component.html',
    styleUrls: ['./xplore-page.component.css'],
    imports: [MobileItemCardComponent, InfiniteScrollDirective, NgOptimizedImage, DatePipe, TranslateModule, BookSectionComponent]
})
export class XplorePageComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild("sliderRef")
  sliderRef?: ElementRef<HTMLElement>;

  @ViewChild("mobilePanelRef")
  mobilePanelRef?: ElementRef<HTMLElement>;

  @ViewChild("mobilePanelStaticRef")
  mobilePanelStaticRef?: ElementRef<HTMLElement>;

  @ViewChild("mobilePanelHandleRef")
  set mobilePanelHandleRef(ref: ElementRef<HTMLElement> | undefined) {
    this.setMobilePanelHandleElement(ref?.nativeElement ?? null);
  }

  @Input()
  xploreItem?: XploreItemDTO;

  slider: KeenSliderInstance | null = null;

  xploreItems = signal<XploreItemDTO[]>([]);
  activeXploreId = signal<number | null>(null);
  desktopActiveMediaIndex = signal<number>(0);
  mobilePanelExpanded = signal<boolean>(false);
  mobilePanelClosing = signal<boolean>(false);
  mobilePanelDragging = signal<boolean>(false);
  mobilePanelCurrentHeight = signal<number>(MOBILE_PANEL_MIN_COLLAPSED_HEIGHT);
  mobileOverlayTab = signal<MobileOverlayTab>('description');
  mobileRelatedItems = signal<SimpleXploreItemDTO[]>([]);
  mobileRelatedItemsLoading = signal<boolean>(false);
  mobileRelatedItemsError = signal<boolean>(false);
  loadingMoreXploreItems = signal<boolean>(false);
  loadedIndex = signal<number>(0);
  currentSliderIndex = signal<number>(0);
  loading = signal<boolean>(false);
  errorHappened = signal<boolean>(false);
  showVerticalSwipeHint = signal<boolean>(false);
  readonly mobileExpandedContentVisible = computed<boolean>(
    () => this.mobilePanelExpanded() ||
      this.mobilePanelClosing() ||
      this.mobilePanelCurrentHeight() > this.mobilePanelCollapsedHeight + 24
  );
  readonly currentXplore = computed<XploreItemDTO | null>(() => {
    const items = this.xploreItems();
    if (!items.length) {
      return null;
    }

    const activeId = this.activeXploreId();
    if (activeId === null) {
      return items[0];
    }

    return items.find(item => item.id === activeId) ?? items[0];
  });
  readonly desktopRecommendedItems = computed<XploreItemDTO[]>(() => {
    const activeId = this.activeXploreId();
    if (activeId === null) {
      return this.xploreItems();
    }

    return this.xploreItems().filter((item) => item.id !== activeId);
  });
  readonly desktopRecommendedSkeletons = Array.from({ length: 6 }, (_, index) => index);

  isMobile = signal<boolean>(true);

  private hasOneSelected = signal<boolean>(false);
  private isPagingRequestInFlight = signal<boolean>(false);
  private hasMoreXploreItems = signal<boolean>(true);
  private readonly canLoadMoreItems = computed(
    () => !this.isPagingRequestInFlight() && this.hasMoreXploreItems()
  );
  private loadMoreStartedAt = 0;
  private hideLoadMoreTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private lastSyncedXploreItemId: number | null = null;
  private pendingSliderUpdateFrame: number | null = null;
  private pendingSliderUpdateTimeout: ReturnType<typeof setTimeout> | null = null;
  private pendingCollapsedHeightFrame: number | null = null;
  private mobilePanelStaticResizeObserver: ResizeObserver | null = null;
  private mobilePanelTouchStartY: number | null = null;
  private mobilePanelStartHeight: number | null = null;
  private mobilePanelCollapsedHeight = MOBILE_PANEL_MIN_COLLAPSED_HEIGHT;
  private readonly mobileBottomBarHeight = 54;
  private readonly mobilePanelTopPadding = 8;
  private readonly mobilePanelGestureToggleDelta = 52;
  private mobileRelatedItemsForId: number | null = null;
  private mobileRelatedItemsCache = new Map<number, SimpleXploreItemDTO[]>();
  private swipeHintHideTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private mobilePanelHandleElement: HTMLElement | null = null;
  private mobilePanelHandleTouchStartListener?: (event: TouchEvent) => void;
  private mobilePanelHandleTouchMoveListener?: (event: TouchEvent) => void;
  private mobilePanelHandleTouchEndListener?: (event: TouchEvent) => void;
  private mobilePanelHandleTouchCancelListener?: (event: TouchEvent) => void;
  private pendingDragHeightFrame: number | null = null;
  private pendingDragHeightValue: number | null = null;
  private readonly intlNumberFormatCache = new Map<string, Intl.NumberFormat>();
  private readonly xploreItemDateCache = new WeakMap<XploreItemDTO, Map<string, Date | null>>();

  private route: ActivatedRoute = inject(ActivatedRoute);
  private xploreItemService: XploreItemService = inject(XploreItemService);
  private metaDataService: MetaDataService = inject(MetaDataService);
  private router: Router = inject(Router);
  private location: Location = inject(Location);
  private analyticsService: AnalyticsService = inject(AnalyticsService);
  private territoryService: TerritoryService = inject(TerritoryService);
  private shareService: ShareService = inject(ShareService);
  private authService: AuthService = inject(AuthService);
  private userService: UserService = inject(UserService);
  private userGeolocationService: UserGeolocationService = inject(UserGeolocationService);
  private translateService: TranslateService = inject(TranslateService);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);
  private ngZone: NgZone = inject(NgZone);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly currentLang = toSignal(this.translateService.onLangChange, {
    initialValue: { lang: this.translateService.currentLang || this.translateService.defaultLang || 'it', translations: {} }
  });

  readonly currentDistanceChipState = computed<DistanceChipState>(() => {
    const xploreItem = this.currentXplore();
    if (!xploreItem) {
      return 'unavailable';
    }
    return this.computeDistanceChipState(xploreItem);
  });

  readonly currentDistanceChipLabel = computed<string>(() => {
    const xploreItem = this.currentXplore();
    if (!xploreItem) {
      return '';
    }
    return this.computeDistanceFromUser(xploreItem) ?? '';
  });

  readonly currentPrimaryTags = computed<TagDTO[]>(() => {
    const xploreItem = this.currentXplore();
    if (!xploreItem) {
      return [];
    }
    return this.computePrimaryTags(xploreItem, 8);
  });

  readonly currentCategoryLabels = computed<string[]>(() => {
    const xploreItem = this.currentXplore();
    if (!xploreItem) {
      return [];
    }
    return this.computeCategoryLabels(xploreItem, 6);
  });

  readonly currentDescriptionHtml = computed<string>(() => {
    const xploreItem = this.currentXplore();
    if (!xploreItem) {
      return '';
    }
    this.currentLang(); // dependency: re-derive when the active language changes
    return this.computeLocalizedDescription(xploreItem);
  });

  private readonly syncMetaTagsEffect = effect(() => {
    this.handleMetaTags(this.currentXplore());
  }, { debugName: 'xploreMetaTagsSync' });
  private readonly syncMobileSliderEffect = effect(() => {
    if (!this.isBrowser || !this.isMobile()) {
      return;
    }
    this.xploreItems(); // dependency: re-sync keen when slides are added or removed
    this.scheduleSliderUpdate();
  }, { debugName: 'xploreMobileSliderSync' });


  ngAfterViewInit(): void {
    this.setupMobilePanelStaticResizeObserver();
    this.initializeMobileSlider();
    this.syncMobileCollapsedHeightToContent();
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.isMobile.set(window.innerWidth < 1024);
    }

    this.loading.set(true);

    if (this.xploreItem) {
      this.appendUniqueItems([this.xploreItem]);
      this.loading.set(false);
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.analyticsService.addXploreVisitAction(Number(id))

      this.hasOneSelected.set(true);
      this.xploreItemService.getXploreItemById(id)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap(xploreItem => {
            if(!xploreItem) {
              this.router.navigate(['/404'], { replaceUrl: true });
              return;
            }
            this.appendUniqueItems([xploreItem]);
            this.syncCurrentXplore(xploreItem);
            this.loading.set(false);
          }),
          catchError(error => {
            this.errorHappened.set(true);
            this.loading.set(false);
            this.router.navigate(['/404'], { replaceUrl: true });
            return EMPTY;
          })
        ).subscribe();
    }

    this.getXploreItemsPaged();
  }

  private changeCurrentUrl(xploreItem: XploreItemDTO): void {
    if (!this.isBrowser) {
      return;
    }

    if(xploreItem.territory.code !== this.territoryService.getTerritoryCodeFromUrl()) {
      if(environment.IS_LOCAL) {
        console.debug("Overwriting territory code")
        return;
      }

      window.location.href = TerritoryService.addTerritoryCodeToUrl(environment.DEFAULT_ITEM_URL + xploreItem.id, xploreItem.territory.code)
      return;
    }

    this.location.go(`/explore/${xploreItem.id}/` + xploreItem.name.toLowerCase()
      .replace(/[&\/\\#, +()$~%.'":*?<>{}]/g, '-')
      .replace(/ /g, `-`)
      .substring(0, 40));
  }

  onSlideChange(changedSliderIndex: number): void {
    if (changedSliderIndex === this.currentSliderIndex()) {
      return;
    }
    this.dismissVerticalSwipeHint();

    const previousSliderIndex = this.currentSliderIndex();
    let direction = previousSliderIndex < changedSliderIndex ? "next" : "prev";
    let currentXplore = this.xploreItems()[changedSliderIndex];
    if (!currentXplore) {
      return;
    }

    this.syncCurrentXplore(currentXplore);

    if (changedSliderIndex == this.xploreItems().length - 3 && direction == "next") {
      this.getXploreItemsPaged();
    }
    this.currentSliderIndex.set(changedSliderIndex);
  }

  private getXploreItemsPaged(): void {
    if (!this.canLoadMoreItems()) {
      return;
    }

    this.startLoadMoreState();
    this.isPagingRequestInFlight.set(true);

    this.xploreItemService.getPagedXploreItems(this.loadedIndex()).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (xploreItems) => {
          this.finishLoadMoreState();

          if (!xploreItems?.length) {
            this.hasMoreXploreItems.set(false);
            return;
          }

          this.appendUniqueItems(xploreItems);
          this.loadedIndex.update((currentIndex) => currentIndex + 1);

          const currentItems = this.xploreItems();
          if (!this.hasOneSelected() && currentItems.length > 0) {
            this.syncCurrentXplore(currentItems[0]);
          }
        },
        error: (error) => {
          this.finishLoadMoreState();
          this.errorHappened.set(true);
          console.debug(error)
        }
      })
  }

  onScroll() {
    this.getXploreItemsPaged();
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    if (!this.isBrowser) {
      return;
    }

    const nextIsMobile = window.innerWidth < 1024;
    if (nextIsMobile === this.isMobile()) {
      if (nextIsMobile) {
        if (this.mobilePanelExpanded()) {
          this.mobilePanelClosing.set(false);
          this.mobilePanelCurrentHeight.set(this.getMobilePanelExpandedHeight());
        }
        this.syncMobileCollapsedHeightToContent();
      }
      return;
    }

    this.isMobile.set(nextIsMobile);
    this.collapseMobilePanel(false);
    this.resetMobileOverlayDetails();

    if (nextIsMobile) {
      this.setupMobilePanelStaticResizeObserver();
      this.syncMobileCollapsedHeightToContent();
      setTimeout(() => this.initializeMobileSlider(), 0);
      return;
    }

    this.disconnectMobilePanelResizeObserver();
    this.destroyMobileSlider();
  }

  ngOnDestroy(): void {
    this.disconnectMobilePanelResizeObserver();
    this.unregisterMobilePanelHandleListeners();
    this.mobilePanelHandleElement = null;
    this.destroyMobileSlider();
    if (this.isBrowser && this.pendingSliderUpdateFrame !== null) {
      cancelAnimationFrame(this.pendingSliderUpdateFrame);
      this.pendingSliderUpdateFrame = null;
    }
    if (this.pendingSliderUpdateTimeout !== null) {
      clearTimeout(this.pendingSliderUpdateTimeout);
      this.pendingSliderUpdateTimeout = null;
    }
    if (this.isBrowser && this.pendingCollapsedHeightFrame !== null) {
      cancelAnimationFrame(this.pendingCollapsedHeightFrame);
      this.pendingCollapsedHeightFrame = null;
    }
    if (this.isBrowser && this.pendingDragHeightFrame !== null) {
      cancelAnimationFrame(this.pendingDragHeightFrame);
      this.pendingDragHeightFrame = null;
    }
    if (this.hideLoadMoreTimeoutId !== null) {
      clearTimeout(this.hideLoadMoreTimeoutId);
      this.hideLoadMoreTimeoutId = null;
    }
    if (this.swipeHintHideTimeoutId !== null) {
      clearTimeout(this.swipeHintHideTimeoutId);
      this.swipeHintHideTimeoutId = null;
    }
  }

  private handleMetaTags(xploreItem: XploreItemDTO | null) {
    this.metaDataService.updateMetaDataByXploreItem(xploreItem, this.resolveMetaRouteUrl(xploreItem));
  }

  private computeDistanceFromUser(xploreItem: XploreItemDTO): string | undefined {
    this.userGeolocationService.currentUserPosition(); // dependency: re-derive when the GPS position arrives
    const distance = this.userGeolocationService.getDistanceXploreItemFromUserLocation(xploreItem);
    if (!distance) {
      return undefined;
    }
    return distance + ' km';
  }

  private computeDistanceChipState(xploreItem: XploreItemDTO): DistanceChipState {
    const latitude = xploreItem?.geoPositionDTO?.latitude;
    const longitude = xploreItem?.geoPositionDTO?.longitude;
    if (latitude === undefined || latitude === null || longitude === undefined || longitude === null) {
      return 'unavailable';
    }

    this.userGeolocationService.currentUserPosition(); // dependency: re-derive when the GPS position arrives
    const distance = this.userGeolocationService.getDistanceXploreItemFromUserLocation(xploreItem);
    if (distance) {
      return 'ready';
    }

    const userLocationStatus: UserLocationStatus = this.userGeolocationService.userLocationStatus();
    return userLocationStatus === 'unavailable' ? 'unavailable' : 'loading';
  }

  setMobileOverlayTab(tab: MobileOverlayTab, currentXplore: XploreItemDTO): void {
    if (tab === 'booking' && !this.isBookableEventItem(currentXplore)) {
      return;
    }

    this.mobileOverlayTab.set(tab);

    if (tab === 'related') {
      this.ensureMobileRelatedItemsLoaded(currentXplore.id);
    }
  }

  isEventItem(xploreItem: XploreItemDTO | null): boolean {
    return xploreItem?.itemType === 'EVENT';
  }

  isBookableEventItem(xploreItem: XploreItemDTO | null): boolean {
    return this.isEventItem(xploreItem) && !!(xploreItem as EventDTO)?.bookable;
  }

  getInfoPositionName(xploreItem: XploreItemDTO): string | null {
    const geoPosition = xploreItem?.geoPositionDTO;
    if (!geoPosition) {
      return null;
    }

    const viaAndStreetNumber = [geoPosition.via, geoPosition.numeroCivico].filter(Boolean).join(' ');
    const fullLocation = [viaAndStreetNumber, geoPosition.comune?.name].filter(Boolean).join(' - ');
    return fullLocation || null;
  }

  getItemPrice(xploreItem: XploreItemDTO): number | null {
    if (xploreItem.itemType === 'COMMERCIAL') {
      return (xploreItem as CommercialDTO).price ?? null;
    }
    if (xploreItem.itemType === 'EVENT') {
      return (xploreItem as EventDTO).price ?? null;
    }
    return null;
  }

  formatPrice(amount: number): string {
    const localeCode = this.translateService.currentLang?.toLowerCase().startsWith('en') ? 'en-US' : 'it-IT';
    let formatter = this.intlNumberFormatCache.get(localeCode);
    if (!formatter) {
      formatter = new Intl.NumberFormat(localeCode, { style: 'currency', currency: 'EUR' });
      this.intlNumberFormatCache.set(localeCode, formatter);
    }
    return formatter.format(amount);
  }

  hasDateInfo(xploreItem: XploreItemDTO): boolean {
    if (xploreItem.itemType === 'EXPLORE') {
      const explore = xploreItem as ExploreDTO;
      return !!(explore.openDate || explore.closeDate);
    }
    if (xploreItem.itemType === 'COMMERCIAL') {
      const commercial = xploreItem as CommercialDTO;
      return !!(commercial.openDate || commercial.closeDate);
    }
    if (xploreItem.itemType === 'EVENT') {
      const event = xploreItem as EventDTO;
      return !!event.date;
    }
    return false;
  }

  getDateStart(xploreItem: XploreItemDTO): Date | null {
    return this.memoizedDate(xploreItem, 'dateStart', () => {
      if (xploreItem.itemType === 'EXPLORE') {
        return this.parseDate((xploreItem as ExploreDTO).openDate);
      }
      if (xploreItem.itemType === 'COMMERCIAL') {
        return this.parseDate((xploreItem as CommercialDTO).openDate);
      }
      if (xploreItem.itemType === 'EVENT') {
        return this.parseDate((xploreItem as EventDTO).date);
      }
      return null;
    });
  }

  getDateEnd(xploreItem: XploreItemDTO): Date | null {
    return this.memoizedDate(xploreItem, 'dateEnd', () => {
      if (xploreItem.itemType === 'EXPLORE') {
        return this.parseDate((xploreItem as ExploreDTO).closeDate);
      }
      if (xploreItem.itemType === 'COMMERCIAL') {
        return this.parseDate((xploreItem as CommercialDTO).closeDate);
      }
      if (xploreItem.itemType === 'EVENT') {
        return this.parseDate((xploreItem as EventDTO).date);
      }
      return null;
    });
  }

  getBookableUntil(xploreItem: XploreItemDTO): Date | null {
    return this.memoizedDate(xploreItem, 'bookableUntil', () => {
      if (xploreItem.itemType === 'EVENT') {
        return this.parseDate((xploreItem as EventDTO).lastBookingTime);
      }
      return null;
    });
  }

  hasHourInfo(xploreItem: XploreItemDTO): boolean {
    if (xploreItem.itemType === 'EXPLORE') {
      const explore = xploreItem as ExploreDTO;
      return !!(explore.openHour || explore.closeHour);
    }
    if (xploreItem.itemType === 'COMMERCIAL') {
      const commercial = xploreItem as CommercialDTO;
      return !!(commercial.openHour || commercial.closeHour);
    }
    if (xploreItem.itemType === 'EVENT') {
      const event = xploreItem as EventDTO;
      return !!(event.openHour || event.closeHour);
    }
    return false;
  }

  getOpenHour(xploreItem: XploreItemDTO): Date | null {
    return this.memoizedDate(xploreItem, 'openHour', () => {
      if (xploreItem.itemType === 'EXPLORE') {
        return this.parseDate((xploreItem as ExploreDTO).openHour);
      }
      if (xploreItem.itemType === 'COMMERCIAL') {
        return this.parseDate((xploreItem as CommercialDTO).openHour);
      }
      if (xploreItem.itemType === 'EVENT') {
        return this.parseDate((xploreItem as EventDTO).openHour);
      }
      return null;
    });
  }

  getCloseHour(xploreItem: XploreItemDTO): Date | null {
    return this.memoizedDate(xploreItem, 'closeHour', () => {
      if (xploreItem.itemType === 'EXPLORE') {
        return this.parseDate((xploreItem as ExploreDTO).closeHour);
      }
      if (xploreItem.itemType === 'COMMERCIAL') {
        return this.parseDate((xploreItem as CommercialDTO).closeHour);
      }
      if (xploreItem.itemType === 'EVENT') {
        return this.parseDate((xploreItem as EventDTO).closeHour);
      }
      return null;
    });
  }

  getRelatedItemImage(item: SimpleXploreItemDTO): string {
    const mediaUrl = item?.multiMediaUrls?.[0];
    return mediaUrl ? `${environment.IMAGES_URL}${mediaUrl}` : ImageManager.getPlaceholderImage();
  }

  onRelatedItemClicked(item: SimpleXploreItemDTO): void {
    if (!item?.id) {
      return;
    }

    this.router.navigate(['explore/' + item.id]);
  }

  trackBySimpleXploreItem(index: number, item: SimpleXploreItemDTO): number {
    return item?.id ?? index;
  }

  onFavoriteButtonClicked(xploreItem: XploreItemDTO) {
    this.authService.isUserLogged().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isUserLogged => {
      if (!isUserLogged) {
        this.router.navigate(['login']);
      } else {
        if (!this.userService.checkIfItemInFavorite(xploreItem.id)) {
          this.userService.addSavedXplore(xploreItem.id);
        } else {
          this.userService.removeFavorite(xploreItem.id);
        }
      }
    });
  }

  onImmersiveButtonClicked(xploreItem: XploreItemDTO) {
    this.router.navigate(['immersive/' + xploreItem.id]);
  }

  onSearchTagClicked(tag: TagDTO) {
    this.router.navigate(['search'], { queryParams: { tag: tag.code } });
  }

  onShareButtonClicked(xploreItem: XploreItemDTO) {
    const title = xploreItem.name;
    const text = xploreItem.shortDescription;

    this.shareService.share(xploreItem.id, title, text,
      TerritoryService.addTerritoryCodeToUrl(environment.DEFAULT_ITEM_URL + xploreItem.id, xploreItem.territory.code)
    );
  }

  onUserProfileClicked(userAccount: UserAccountDTO) {
    this.router.navigate(['profile/' + userAccount.userId]);
  }

  isItemFavorite(xploreItem: XploreItemDTO) {
    return this.userService.checkIfItemInFavorite(xploreItem.id);
  }

  get canShare(): boolean {
    return this.shareService.canShare();
  }

  onRedirectToInfoPageClicked(xploreItem: XploreItemDTO) {
    this.router.navigate(['explore/info/' + xploreItem.id]);
  }

  onNavigateToMapClicked(xploreItem: XploreItemDTO) {
    this.router.navigate(['map/' + xploreItem.id]);
  }

  onEventBookingQuickAction(xploreItem: XploreItemDTO): void {
    if (!this.isBookableEventItem(xploreItem)) {
      return;
    }

    this.setMobileOverlayTab('booking', xploreItem);
    this.mobilePanelClosing.set(false);
    this.mobilePanelExpanded.set(true);
    this.mobilePanelCurrentHeight.set(this.getMobilePanelExpandedHeight());
  }

  /**
   * Touch handlers are registered as native listeners outside the Angular
   * zone (see registerMobilePanelHandleListeners) so that drag-driven
   * touchmove events don't trigger a full change detection on every frame.
   */
  private handleMobilePanelHandleTouchStart(event: TouchEvent): void {
    if (!event.touches.length) {
      return;
    }

    this.mobilePanelTouchStartY = event.touches[0].clientY;
    this.mobilePanelStartHeight = this.mobilePanelCurrentHeight();

    this.ngZone.run(() => {
      this.mobilePanelClosing.set(false);
      this.mobilePanelDragging.set(true);
    });
  }

  private handleMobilePanelHandleTouchMove(event: TouchEvent): void {
    if (this.mobilePanelTouchStartY === null || this.mobilePanelStartHeight === null || !event.touches.length) {
      return;
    }

    const currentY = event.touches[0].clientY;
    const deltaY = currentY - this.mobilePanelTouchStartY;
    const nextHeight = this.clamp(
      this.mobilePanelStartHeight - deltaY,
      this.mobilePanelCollapsedHeight,
      this.getMobilePanelExpandedHeight()
    );

    if (event.cancelable) {
      event.preventDefault();
    }

    const panel = this.mobilePanelRef?.nativeElement;
    if (panel) {
      panel.style.height = `${nextHeight}px`;
    }

    this.scheduleDragHeightCommit(nextHeight);
  }

  private handleMobilePanelHandleTouchEnd(): void {
    const startHeight = this.mobilePanelStartHeight;
    this.mobilePanelTouchStartY = null;
    this.mobilePanelStartHeight = null;

    if (this.pendingDragHeightFrame !== null) {
      cancelAnimationFrame(this.pendingDragHeightFrame);
      this.pendingDragHeightFrame = null;
    }
    const pendingHeight = this.pendingDragHeightValue;
    this.pendingDragHeightValue = null;

    this.ngZone.run(() => {
      this.mobilePanelDragging.set(false);

      if (startHeight === null) {
        return;
      }

      // Flush the latest drag position (it may not have been committed yet
      // by the rAF throttle) so the toggle threshold below sees it.
      if (pendingHeight !== null) {
        this.mobilePanelCurrentHeight.set(pendingHeight);
      }

      const expandedHeight = this.getMobilePanelExpandedHeight();
      const currentHeight = this.mobilePanelCurrentHeight();
      const heightDelta = currentHeight - startHeight;
      const expandThreshold = this.mobilePanelCollapsedHeight + (expandedHeight - this.mobilePanelCollapsedHeight) * 0.45;

      // A decisive swipe wins over the position threshold.
      const shouldExpand = Math.abs(heightDelta) >= this.mobilePanelGestureToggleDelta
        ? heightDelta > 0
        : currentHeight >= expandThreshold;

      if (shouldExpand) {
        this.mobilePanelClosing.set(false);
        this.mobilePanelExpanded.set(true);
        this.mobilePanelCurrentHeight.set(expandedHeight);
      } else {
        this.collapseMobilePanel(true);
      }
    });
  }

  /**
   * Throttles mobilePanelCurrentHeight signal writes to once per animation
   * frame during a drag, so mobileExpandedContentVisible (and the
   * --dragging-driven DOM height) stay correct without a CD per touchmove.
   */
  private scheduleDragHeightCommit(height: number): void {
    this.pendingDragHeightValue = height;

    if (this.pendingDragHeightFrame !== null) {
      return;
    }

    this.pendingDragHeightFrame = requestAnimationFrame(() => {
      this.pendingDragHeightFrame = null;
      const pendingHeight = this.pendingDragHeightValue;
      this.pendingDragHeightValue = null;

      if (pendingHeight === null) {
        return;
      }

      this.ngZone.run(() => {
        this.mobilePanelCurrentHeight.set(pendingHeight);
      });
    });
  }

  onMobilePanelTransitionEnd(event: TransitionEvent): void {
    if (event.propertyName !== 'height') {
      return;
    }

    if (!this.mobilePanelExpanded()) {
      this.mobilePanelClosing.set(false);
    }
  }

  getCoverImage(xploreItem: XploreItemDTO): string {
    const mediaUrl = xploreItem?.multiMedia?.[0]?.mediaURL;
    return mediaUrl ? `${environment.IMAGES_URL}${mediaUrl}` : ImageManager.getPlaceholderImage();
  }

  private computePrimaryTags(xploreItem: XploreItemDTO, max = 4): TagDTO[] {
    return (xploreItem?.tags ?? []).filter(tag => tag.tagLevel === 0).slice(0, max);
  }

  getLocationLabel(xploreItem: XploreItemDTO): string | null {
    const comune = xploreItem?.geoPositionDTO?.comune?.name;
    const provincia = xploreItem?.geoPositionDTO?.provincia?.name;
    const location = xploreItem?.geoPositionDTO?.location?.name;

    return [location, comune, provincia].filter(Boolean).join(' · ') || null;
  }

  getSummary(xploreItem: XploreItemDTO): string {
    return xploreItem?.shortDescription || xploreItem?.description || '';
  }

  getDesktopMediaCount(xploreItem: XploreItemDTO): number {
    return xploreItem?.multiMedia?.length ?? 0;
  }

  getDesktopMediaUrl(xploreItem: XploreItemDTO, index: number): string {
    const mediaCount = this.getDesktopMediaCount(xploreItem);
    if (!mediaCount) {
      return ImageManager.getPlaceholderImage();
    }

    const normalizedIndex = this.normalizeDesktopMediaIndex(index, mediaCount);
    const mediaUrl = xploreItem.multiMedia?.[normalizedIndex]?.mediaURL;
    return mediaUrl ? `${environment.IMAGES_URL}${mediaUrl}` : ImageManager.getPlaceholderImage();
  }

  setDesktopMediaIndex(xploreItem: XploreItemDTO, index: number): void {
    const mediaCount = this.getDesktopMediaCount(xploreItem);
    if (!mediaCount) {
      this.desktopActiveMediaIndex.set(0);
      return;
    }

    this.desktopActiveMediaIndex.set(this.normalizeDesktopMediaIndex(index, mediaCount));
  }

  showPreviousDesktopMedia(xploreItem: XploreItemDTO): void {
    this.setDesktopMediaIndex(xploreItem, this.desktopActiveMediaIndex() - 1);
  }

  showNextDesktopMedia(xploreItem: XploreItemDTO): void {
    this.setDesktopMediaIndex(xploreItem, this.desktopActiveMediaIndex() + 1);
  }

  onDesktopRecommendedItemClicked(xploreItem: XploreItemDTO): void {
    if (!xploreItem) {
      return;
    }

    this.syncCurrentXplore(xploreItem);
    if (this.isBrowser) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  private computeLocalizedDescription(xploreItem: XploreItemDTO): string {
    const currentLang = (this.translateService.currentLang || this.translateService.defaultLang || 'it').toLowerCase();

    if (currentLang.startsWith('en')) {
      return xploreItem?.descriptionEngFormatted || xploreItem?.descriptionEng || xploreItem?.descriptionFormatted || xploreItem?.description || '';
    }

    return xploreItem?.descriptionFormatted || xploreItem?.description || xploreItem?.descriptionEngFormatted || xploreItem?.descriptionEng || '';
  }

  trackByTag(index: number, tag: TagDTO): number {
    return tag?.id ?? index;
  }

  trackByLabel(index: number, label: string): string {
    return label || String(index);
  }

  private computeCategoryLabels(xploreItem: XploreItemDTO, max = 4): string[] {
    return (xploreItem?.categories ?? [])
      .map((category: Category) => this.formatCodeAsLabel(category.code))
      .filter(Boolean)
      .slice(0, max);
  }

  normalizeWebsiteUrl(website: string): string {
    if (!website) {
      return '#';
    }

    return /^https?:\/\//i.test(website) ? website : `https://${website}`;
  }

  private initializeMobileSlider(): void {
    if (!this.isBrowser || !this.isMobile() || !this.sliderRef?.nativeElement || this.slider) {
      return;
    }

    this.slider = new KeenSlider(this.sliderRef.nativeElement, createFeedSliderOptions({
      vertical: true,
      // 'performance' rounds slide sizes/offsets to whole px, but the 100dvh
      // container is usually fractional on mobile: the rounding error leaves a
      // ~1px sliver of the previous slide visible at the top after each snap.
      renderMode: 'precision',
      slideChanged: (slider) => {
        const changedSliderIndex = slider.track.details?.rel;
        if (typeof changedSliderIndex === 'number') {
          this.onSlideChange(changedSliderIndex);
        }
      },
    }));

    this.scheduleSliderUpdate();
  }

  private scheduleSliderUpdate(): void {
    if (!this.isBrowser || !this.slider) {
      return;
    }

    if (this.pendingSliderUpdateFrame !== null) {
      cancelAnimationFrame(this.pendingSliderUpdateFrame);
    }

    // The animator can stay active for many frames during a swipe; run the
    // re-check loop outside the zone so each frame doesn't trigger a full CD.
    this.ngZone.runOutsideAngular(() => {
      this.pendingSliderUpdateFrame = requestAnimationFrame(() => {
        this.pendingSliderUpdateFrame = null;
        if (!this.slider) {
          return;
        }
        if (this.slider.animator.active) {
          this.scheduleSliderUpdate();
          return;
        }
        // update() fully re-initializes keen: only pay that cost when slides were
        // actually added/removed, not on every swipe-driven signal change.
        if (this.slider.slides.length !== this.xploreItems().length) {
          this.commitSliderUpdateFromTask();
        }
      });
    });
  }

  /**
   * keen's update() synchronously strips every slide's inline transform and
   * re-applies them inside a requestAnimationFrame. Invoked from within a rAF
   * callback (as the idle-check above is), keen's rAF lands on the NEXT frame
   * and the browser paints one frame with no transforms: every slide falls
   * back to its natural flow position and slide 0 (the first item's first
   * image) flashes fullscreen. Committing from a macrotask instead guarantees
   * keen's rAF runs before the next paint. Runs outside the Angular zone
   * (called from the rAF above); only the update itself re-enters the zone.
   */
  private commitSliderUpdateFromTask(): void {
    if (this.pendingSliderUpdateTimeout !== null) {
      return;
    }

    this.pendingSliderUpdateTimeout = setTimeout(() => {
      this.pendingSliderUpdateTimeout = null;
      if (!this.slider) {
        return;
      }
      // A swipe may have started between the idle check and this task.
      if (this.slider.animator.active) {
        this.scheduleSliderUpdate();
        return;
      }

      const itemCount = this.xploreItems().length;
      if (this.slider.slides.length === itemCount) {
        return;
      }

      const maxIndex = Math.max(0, itemCount - 1);
      const targetIndex = this.clamp(this.currentSliderIndex(), 0, maxIndex);
      // update() can synchronously trigger slideChanged -> onSlideChange,
      // which writes signals, so re-enter the zone for it.
      this.ngZone.run(() => {
        this.slider?.update(undefined, targetIndex);
      });
    }, 0);
  }

  private destroyMobileSlider(): void {
    this.slider?.destroy();
    this.slider = null;
  }

  private setMobilePanelHandleElement(element: HTMLElement | null): void {
    if (this.mobilePanelHandleElement === element) {
      return;
    }

    this.unregisterMobilePanelHandleListeners();
    this.mobilePanelHandleElement = element;

    if (element && this.isBrowser) {
      this.registerMobilePanelHandleListeners(element);
    }
  }

  /**
   * Registered outside the Angular zone: touchmove fires at up to ~60/s
   * during a drag, and we don't want each event to schedule a full CD.
   */
  private registerMobilePanelHandleListeners(element: HTMLElement): void {
    this.ngZone.runOutsideAngular(() => {
      this.mobilePanelHandleTouchStartListener = (event: TouchEvent) => this.handleMobilePanelHandleTouchStart(event);
      this.mobilePanelHandleTouchMoveListener = (event: TouchEvent) => this.handleMobilePanelHandleTouchMove(event);
      this.mobilePanelHandleTouchEndListener = () => this.handleMobilePanelHandleTouchEnd();
      this.mobilePanelHandleTouchCancelListener = () => this.handleMobilePanelHandleTouchEnd();

      element.addEventListener('touchstart', this.mobilePanelHandleTouchStartListener, { passive: true });
      element.addEventListener('touchmove', this.mobilePanelHandleTouchMoveListener, { passive: false });
      element.addEventListener('touchend', this.mobilePanelHandleTouchEndListener, { passive: true });
      element.addEventListener('touchcancel', this.mobilePanelHandleTouchCancelListener, { passive: true });
    });
  }

  private unregisterMobilePanelHandleListeners(): void {
    const element = this.mobilePanelHandleElement;
    if (!element) {
      return;
    }

    if (this.mobilePanelHandleTouchStartListener) {
      element.removeEventListener('touchstart', this.mobilePanelHandleTouchStartListener);
    }
    if (this.mobilePanelHandleTouchMoveListener) {
      element.removeEventListener('touchmove', this.mobilePanelHandleTouchMoveListener);
    }
    if (this.mobilePanelHandleTouchEndListener) {
      element.removeEventListener('touchend', this.mobilePanelHandleTouchEndListener);
    }
    if (this.mobilePanelHandleTouchCancelListener) {
      element.removeEventListener('touchcancel', this.mobilePanelHandleTouchCancelListener);
    }

    this.mobilePanelHandleTouchStartListener = undefined;
    this.mobilePanelHandleTouchMoveListener = undefined;
    this.mobilePanelHandleTouchEndListener = undefined;
    this.mobilePanelHandleTouchCancelListener = undefined;
  }

  private disconnectMobilePanelResizeObserver(): void {
    this.mobilePanelStaticResizeObserver?.disconnect();
    this.mobilePanelStaticResizeObserver = null;
  }

  private syncCurrentXplore(xploreItem: XploreItemDTO): void {
    if (!xploreItem || this.lastSyncedXploreItemId === xploreItem.id) {
      return;
    }

    this.lastSyncedXploreItemId = xploreItem.id;
    this.activeXploreId.set(xploreItem.id);
    this.desktopActiveMediaIndex.set(0);
    this.collapseMobilePanel(true);
    this.resetMobileOverlayDetails();
    this.syncMobileCollapsedHeightToContent();
    this.changeCurrentUrl(xploreItem);
  }

  private appendUniqueItems(items: XploreItemDTO[]): XploreItemDTO[] {
    const currentItems = this.xploreItems();
    const existingIds = new Set(currentItems.map(item => item.id));
    const freshItems = items.filter(item => {
      if (existingIds.has(item.id)) {
        return false;
      }
      existingIds.add(item.id);
      return true;
    });
    this.xploreItems.set([...currentItems, ...freshItems]);
    this.maybeShowVerticalSwipeHint();
    return freshItems;
  }

  /**
   * Shown once per user (localStorage) the first time the feed has more than
   * one item to swipe through; auto-hides after a few seconds, and the first
   * real swipe marks it as learned for good.
   */
  private maybeShowVerticalSwipeHint(): void {
    if (!this.isBrowser || !this.isMobile() || this.showVerticalSwipeHint() || this.xploreItems().length < 2) {
      return;
    }

    try {
      if (localStorage.getItem(SWIPE_HINT_STORAGE_KEY)) {
        return;
      }
    } catch {
      return;
    }

    this.showVerticalSwipeHint.set(true);
    this.swipeHintHideTimeoutId = setTimeout(() => {
      this.showVerticalSwipeHint.set(false);
      this.swipeHintHideTimeoutId = null;
    }, 4500);
  }

  private dismissVerticalSwipeHint(): void {
    if (this.swipeHintHideTimeoutId !== null) {
      clearTimeout(this.swipeHintHideTimeoutId);
      this.swipeHintHideTimeoutId = null;
    }
    if (this.showVerticalSwipeHint()) {
      this.showVerticalSwipeHint.set(false);
    }
    try {
      localStorage.setItem(SWIPE_HINT_STORAGE_KEY, '1');
    } catch {
      // storage unavailable (private mode): the hint will just show again
    }
  }

  private resolveMetaRouteUrl(xploreItem: XploreItemDTO | null): string {
    const routeId = this.route.snapshot.paramMap.get('id');
    const routeSlug = this.route.snapshot.paramMap.get('metaName');

    if (routeId) {
      if (routeSlug) {
        return `/explore/${routeId}/${routeSlug}`;
      }
      return `/explore/${routeId}`;
    }

    if (xploreItem?.id) {
      return `/explore/${xploreItem.id}`;
    }

    return this.router.url || '/explore';
  }

  private formatCodeAsLabel(code?: string | null): string {
    if (!code) {
      return '';
    }

    return code
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  private getMobilePanelExpandedHeight(): number {
    if (!this.isBrowser) {
      return 560;
    }

    const panelBottomOffset = this.getMobilePanelBottomOffset();
    const calculatedHeight = window.innerHeight - panelBottomOffset - this.mobilePanelTopPadding;
    return Math.max(this.mobilePanelCollapsedHeight + 80, calculatedHeight);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private getMobilePanelBottomOffset(): number {
    if (!this.mobilePanelRef?.nativeElement) {
      return this.mobileBottomBarHeight + 8;
    }

    const computedBottomValue = window.getComputedStyle(this.mobilePanelRef.nativeElement).bottom;
    const parsedBottom = Number.parseFloat(computedBottomValue);

    if (Number.isFinite(parsedBottom)) {
      return parsedBottom;
    }

    return this.mobileBottomBarHeight + 8;
  }

  private resetMobileOverlayDetails(): void {
    this.mobileOverlayTab.set('description');
    this.mobileRelatedItems.set([]);
    this.mobileRelatedItemsLoading.set(false);
    this.mobileRelatedItemsError.set(false);
    this.mobileRelatedItemsForId = null;
  }

  private syncMobileCollapsedHeightToContent(): void {
    if (!this.isBrowser || !this.isMobile()) {
      return;
    }

    if (this.pendingCollapsedHeightFrame !== null) {
      cancelAnimationFrame(this.pendingCollapsedHeightFrame);
    }

    this.pendingCollapsedHeightFrame = requestAnimationFrame(() => {
      this.pendingCollapsedHeightFrame = null;

      const panel = this.mobilePanelRef?.nativeElement;
      const staticSection = this.mobilePanelStaticRef?.nativeElement;
      if (!panel || !staticSection) {
        return;
      }

      if (!this.mobilePanelStaticResizeObserver) {
        this.setupMobilePanelStaticResizeObserver();
      }

      const panelStyles = window.getComputedStyle(panel);
      const paddingTop = Number.parseFloat(panelStyles.paddingTop) || 0;
      const paddingBottom = Number.parseFloat(panelStyles.paddingBottom) || 0;
      const measuredCollapsedHeight = Math.ceil(staticSection.scrollHeight + paddingTop + paddingBottom);
      const nextCollapsedHeight = this.clamp(
        measuredCollapsedHeight,
        MOBILE_PANEL_MIN_COLLAPSED_HEIGHT,
        MOBILE_PANEL_MAX_COLLAPSED_HEIGHT
      );

      // Ignore sub-2px jitter from the ResizeObserver.
      if (Math.abs(nextCollapsedHeight - this.mobilePanelCollapsedHeight) >= 2) {
        this.mobilePanelCollapsedHeight = nextCollapsedHeight;
      }

      if (!this.mobilePanelExpanded() && !this.mobilePanelDragging()) {
        this.mobilePanelCurrentHeight.set(this.mobilePanelCollapsedHeight);
      }
    });
  }

  private setupMobilePanelStaticResizeObserver(): void {
    if (!this.isBrowser || !this.isMobile()) {
      return;
    }

    if (this.mobilePanelStaticResizeObserver) {
      return;
    }

    const staticSection = this.mobilePanelStaticRef?.nativeElement;
    if (!staticSection || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.mobilePanelStaticResizeObserver = new ResizeObserver(() => {
      this.syncMobileCollapsedHeightToContent();
    });

    this.mobilePanelStaticResizeObserver.observe(staticSection);
  }

  private ensureMobileRelatedItemsLoaded(xploreItemId: number): void {
    const cachedItems = this.mobileRelatedItemsCache.get(xploreItemId);
    if (cachedItems) {
      this.mobileRelatedItemsForId = xploreItemId;
      this.mobileRelatedItems.set(cachedItems);
      this.mobileRelatedItemsLoading.set(false);
      this.mobileRelatedItemsError.set(false);
      return;
    }

    if (this.mobileRelatedItemsLoading() && this.mobileRelatedItemsForId === xploreItemId) {
      return;
    }

    this.mobileRelatedItemsForId = xploreItemId;
    this.mobileRelatedItemsLoading.set(true);
    this.mobileRelatedItemsError.set(false);
    this.mobileRelatedItems.set([]);

    this.xploreItemService.getNearbySimpleXploreItems(xploreItemId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (items) => {
          if (this.mobileRelatedItemsForId !== xploreItemId) {
            return;
          }
          const relatedItems = items ?? [];
          this.mobileRelatedItemsCache.set(xploreItemId, relatedItems);
          this.mobileRelatedItems.set(relatedItems);
          this.mobileRelatedItemsLoading.set(false);
        },
        error: () => {
          if (this.mobileRelatedItemsForId !== xploreItemId) {
            return;
          }
          this.mobileRelatedItemsError.set(true);
          this.mobileRelatedItemsLoading.set(false);
          this.mobileRelatedItems.set([]);
        }
      });
  }

  private parseDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const parsedDate = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  /**
   * Caches parsed Date instances per xplore item and field so the same Date
   * object identity is returned across change-detection cycles, preserving
   * DatePipe's memoization.
   */
  private memoizedDate(xploreItem: XploreItemDTO, key: string, compute: () => Date | null): Date | null {
    let fieldsCache = this.xploreItemDateCache.get(xploreItem);
    if (!fieldsCache) {
      fieldsCache = new Map<string, Date | null>();
      this.xploreItemDateCache.set(xploreItem, fieldsCache);
    }

    if (fieldsCache.has(key)) {
      return fieldsCache.get(key) ?? null;
    }

    const value = compute();
    fieldsCache.set(key, value);
    return value;
  }

  private normalizeDesktopMediaIndex(index: number, mediaCount: number): number {
    return ((index % mediaCount) + mediaCount) % mediaCount;
  }

  private startLoadMoreState(): void {
    this.loadMoreStartedAt = Date.now();
    if (this.hideLoadMoreTimeoutId !== null) {
      clearTimeout(this.hideLoadMoreTimeoutId);
      this.hideLoadMoreTimeoutId = null;
    }
    this.loadingMoreXploreItems.set(true);
  }

  private finishLoadMoreState(): void {
    this.isPagingRequestInFlight.set(false);

    const elapsedMs = Date.now() - this.loadMoreStartedAt;
    const remainingMs = Math.max(0, 180 - elapsedMs);

    if (remainingMs === 0) {
      this.loadingMoreXploreItems.set(false);
      return;
    }

    this.hideLoadMoreTimeoutId = setTimeout(() => {
      this.loadingMoreXploreItems.set(false);
      this.hideLoadMoreTimeoutId = null;
    }, remainingMs);
  }

  private collapseMobilePanel(keepContentVisibleUntilTransitionEnd: boolean): void {
    const shouldKeepContentVisible =
      keepContentVisibleUntilTransitionEnd && this.mobilePanelCurrentHeight() > this.mobilePanelCollapsedHeight + 1;

    this.mobilePanelExpanded.set(false);
    this.mobilePanelDragging.set(false);
    this.mobilePanelClosing.set(shouldKeepContentVisible);
    this.mobilePanelCurrentHeight.set(this.mobilePanelCollapsedHeight);
  }

  protected readonly ImageManager = ImageManager;
}
